import { FC, useEffect, useState, useMemo, useContext } from 'react';
import { VendorPageContext } from '../../../context';
import { Box, Button, Link, useMediaQuery } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { Table, Pagination } from '../../../components';
import { getInProgressPandaDocContracts } from '../../../fetch';
import { useSnackbar } from 'notistack';
import { formatDate, formatShortFriendlyDateWithTime } from '../../../helpers';
import { IDateRange, IVendorContract } from '../../../models';
import { VendorPage } from '../VendorPage';
import { MobileInProgressContracts } from './MobileInProgressContracts';
import { InProgressContractFilters } from './InProgressContractFilters';

interface IInProgressContracts {}

export const InProgressContracts: FC<IInProgressContracts> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { loadingVendors, vendorSageId } = useContext(VendorPageContext);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingContracts, setLoadingContracts] = useState<boolean>(true);
  const [contracts, setContracts] = useState<IVendorContract[]>([]);

  const [maxContractAge, setMaxContractAge] = useState<number | undefined>(undefined);

  const [selectedSort, setSelectedSort] = useState<string>('startDate');
  const [sortDirection, setSortDirection] = useState<{
    vendorName?: 'Asc' | 'Desc';
    pandaDocContractType?: 'Asc' | 'Desc';
    pandaDocContractName?: 'Asc' | 'Desc';
    startDate?: 'Asc' | 'Desc';
    contractAge?: 'Asc' | 'Desc';
    lastChange?: 'Asc' | 'Desc';
  }>({
    startDate: 'Desc'
  });

  const [vendorSearchValue, setVendorSearchValue] = useState('');
  const [contractSearchValue, setContractSearchValue] = useState('');
  const [selectedContractType, setSelectedContractType] = useState('');
  const [selectedDateStartedRange, setSelectedDateStartedRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [selectedContractAge, setSelectedContractAge] = useState<number[]>([0, 0]);
  const [selectedLastChangedRange, setSelectedLastChangedRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [filters, setFilters] = useState<{
    vendorSearch?: string;
    pandaDocContractType?: string;
    pandaDocContractName?: string;
    startDate?: string;
    endDate?: string;
    contractAgeStart?: number | null;
    contractAgeEnd?: number | null;
    lastChangeStart?: string;
    lastChangeEnd?: string;
  } | null>({});

  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchInProgressContracts = async () => {
    try {
      setLoadingContracts(true);
      const res = await getInProgressPandaDocContracts({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        vendorSageId: vendorSageId || null,
        ...filters,
        vendorSearch: !vendorSageId ? vendorSearchValue : ''
      });
      setRecordCount(res.totalRecordCount);
      setContracts(res.records);
      // Ensure that the maxContractAge is set only once
      if (!maxContractAge) {
        setMaxContractAge(res.maximumContractAge);
      }
    } catch (error) {
      enqueueSnackbar(`Error loading contracts, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingContracts(false);
    }
  };
  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleResetFilters = () => {
    setFilters({
      vendorSearch: '',
      pandaDocContractType: '',
      pandaDocContractName: '',
      startDate: '',
      endDate: '',
      contractAgeStart: null,
      contractAgeEnd: null,
      lastChangeStart: '',
      lastChangeEnd: ''
    });
    setSelectedContractAge([0, 0]);
  };

  useEffect(() => {
    fetchInProgressContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSageId, page, perPage, filters, sortDirection, selectedSort]);

  useEffect(() => {
    // Reset vendorSearch filter when the vendorSageId is updated
    if (!!vendorSageId) {
      setFilters({ ...filters, vendorSearch: '' });
      setVendorSearchValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSageId]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Vendor / Pending Vendor',
        accessor: 'vendorName',
        isServerSorted: selectedSort === 'vendorName',
        isServerSortedDesc: sortDirection.vendorName === 'Desc',
        handleClickColumn: () => handleClickColumn('vendorName')
      },
      {
        Header: 'Contract Type',
        accessor: 'pandaDocContractType',
        isServerSorted: selectedSort === 'pandaDocContractType',
        isServerSortedDesc: sortDirection.pandaDocContractType === 'Desc',
        handleClickColumn: () => handleClickColumn('pandaDocContractType')
      },
      {
        Header: 'Contract Name',
        accessor: 'pandaDocContractName',
        isServerSorted: selectedSort === 'pandaDocContractName',
        isServerSortedDesc: sortDirection.pandaDocContractName === 'Desc',
        handleClickColumn: () => handleClickColumn('pandaDocContractName')
      },
      {
        Header: 'Status',
        accessor: 'pandaDocDocumentStatuses',
        sort: false
      },
      {
        Header: 'Date Started',
        accessor: 'dateStarted',
        isServerSorted: selectedSort === 'startDate',
        isServerSortedDesc: sortDirection.startDate === 'Desc',
        handleClickColumn: () => handleClickColumn('startDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorContract } };
        }) => {
          return <>{formatDate(original.dateStarted)}</>;
        }
      },
      {
        Header: 'Contract Age',
        accessor: 'contractAge',
        isServerSorted: selectedSort === 'contractAge',
        isServerSortedDesc: sortDirection.contractAge === 'Desc',
        handleClickColumn: () => handleClickColumn('contractAge')
      },
      {
        Header: 'Last Changed',
        accessor: 'lastChange',
        isServerSorted: selectedSort === 'lastChange',
        isServerSortedDesc: sortDirection.lastChange === 'Desc',
        handleClickColumn: () => handleClickColumn('lastChange'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorContract } };
        }) => {
          return <>{formatShortFriendlyDateWithTime(original.lastChange)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorContract } };
        }) => {
          return (
            <Box display="flex" textAlign="right">
              <Button
                id="edit"
                color="primary"
                startIcon={<Visibility />}
                LinkComponent={Link}
                href={original.linkToDocument}
                target="_blank"
              >
                View
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts]);

  return (
    <VendorPage>
      <InProgressContractFilters
        isLoading={isLoadingContracts || loadingVendors}
        handleVendorSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            vendorSearch: vendorSearchValue,
            pandaDocContractType: selectedContractType,
            pandaDocContractName: contractSearchValue,
            startDate: selectedDateStartedRange?.startDate?.toISOString() || undefined,
            endDate: selectedDateStartedRange?.endDate?.toISOString() || undefined,
            contractAgeStart: selectedContractAge[0],
            contractAgeEnd: selectedContractAge[1],
            lastChangeStart: selectedLastChangedRange?.startDate?.toISOString() || undefined,
            lastChangeEnd: selectedLastChangedRange?.endDate?.toISOString() || undefined
          });

          if (clearSearch) {
            setVendorSearchValue('');
          }
        }}
        handleContractSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            vendorSearch: vendorSearchValue,
            pandaDocContractType: selectedContractType,
            pandaDocContractName: contractSearchValue,
            startDate: selectedDateStartedRange?.startDate?.toISOString() || undefined,
            endDate: selectedDateStartedRange?.endDate?.toISOString() || undefined,
            contractAgeStart:
              selectedContractAge[0] > 0 || selectedContractAge[1] > 0
                ? selectedContractAge[0]
                : null,
            contractAgeEnd:
              selectedContractAge[0] > 0 || selectedContractAge[1] > 0
                ? selectedContractAge[1]
                : null,
            lastChangeStart: selectedLastChangedRange?.startDate?.toISOString() || undefined,
            lastChangeEnd: selectedLastChangedRange?.endDate?.toISOString() || undefined
          });
          if (clearSearch) {
            setContractSearchValue('');
          }
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            handleResetFilters();
          } else {
            setPage(0);
            setFilters({
              ...filters,
              vendorSearch: vendorSearchValue || undefined,
              pandaDocContractType: selectedContractType || undefined,
              pandaDocContractName: contractSearchValue || undefined,
              startDate: selectedDateStartedRange?.startDate?.toISOString() || undefined,
              endDate: selectedDateStartedRange?.endDate?.toISOString() || undefined,
              contractAgeStart:
                selectedContractAge[0] > 0 || selectedContractAge[1] > 0
                  ? selectedContractAge[0]
                  : null,
              contractAgeEnd:
                selectedContractAge[0] > 0 || selectedContractAge[1] > 0
                  ? selectedContractAge[1]
                  : null,
              lastChangeStart: selectedLastChangedRange?.startDate?.toISOString() || undefined,
              lastChangeEnd: selectedLastChangedRange?.endDate?.toISOString() || undefined
            });
          }
        }}
        setVendorSearchValue={setVendorSearchValue}
        vendorSearchValue={vendorSearchValue}
        setSelectedContractType={setSelectedContractType}
        selectedContractType={selectedContractType}
        setContractSearchValue={setContractSearchValue}
        contractSearchValue={contractSearchValue}
        setSelectedDateStartedRange={setSelectedDateStartedRange}
        selectedDateStartedRange={selectedDateStartedRange}
        setSelectedContractAge={setSelectedContractAge}
        selectedContractAge={selectedContractAge}
        setSelectedLastChangedRange={setSelectedLastChangedRange}
        selectedLastChangedRange={selectedLastChangedRange}
        maxContractAge={maxContractAge}
        vendorSageId={vendorSageId}
      />

      <Table
        columns={columns}
        data={contracts}
        isLoading={isLoadingContracts || loadingVendors}
        hidePagination
        noResultsText="No in-progress contracts to display"
        ResponsiveComponent={isMobile ? MobileInProgressContracts : undefined}
      />
      {!isLoadingContracts && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </VendorPage>
  );
};
